import { useSyncExternalStore } from 'use-sync-external-store/shim';
import http from '../services/utils/http-common';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ResourceState<T> {
  data: T[];
  loading: boolean;
  working: boolean;
  loaded: boolean;
  error: string | null;
}

export interface ResourceStore<T extends { id?: string }> {
  /** React hook — subscribe a component to this store */
  useStore: () => ResourceState<T>;
  /** Raw snapshot access (outside components, e.g. in WS updater) */
  getSnapshot: () => ResourceState<T>;
  /**
   * Load the full collection from the API once. No-op if already loaded.
   * Call force=true to reload even if already loaded (e.g. after an import).
   */
  load: (force?: boolean) => Promise<void>;
  /** Find a single item by id (in-memory, no API call) */
  getById: (id: string) => T | undefined;
  /** Filter items in-memory (no API call) */
  getFiltered: (predicate: (item: T) => boolean) => T[];
  /** POST (new) or PUT (existing, requires id) */
  save: (item: T) => Promise<T>;
  /** DELETE by id */
  deleteById: (id: string) => Promise<void>;
  /** Apply a WS event directly to state (used by useStoreUpdater) */
  applyCreate: (item: T) => void;
  applyUpdate: (item: T) => void;
  applyDelete: (id: string) => void;
}

// ─── Factory ──────────────────────────────────────────────────────────────────

/**
 * Creates a self-contained, reactive resource store backed by the REST API.
 *
 * Design principle: load the full collection ONCE, filter in-memory.
 * Never pass query params to load() — use getFiltered() or filter store.data directly.
 *
 * Usage:
 *   export const quoteStore = createResourceStore<Quote>('/quotes');
 *
 * In components:
 *   const { data: quotes, loading } = quoteStore.useStore();
 *   const myQuotes = quotes.filter(q => q.businessid === businessId);
 *
 *   // or via helper:
 *   const myQuotes = quoteStore.getFiltered(q => q.businessid === businessId);
 */
export function createResourceStore<T extends { id?: string }>(resourceUri: string): ResourceStore<T> {
  // ── Internal state (module-level singleton) ──────────────────────────────
  let state: ResourceState<T> = {
    data: [],
    loading: false,
    working: false,
    loaded: false,
    error: null,
  };

  const listeners = new Set<() => void>();

  const setState = (patch: Partial<ResourceState<T>>) => {
    state = { ...state, ...patch };
    listeners.forEach(l => l());
  };

  // ── useSyncExternalStore wiring ──────────────────────────────────────────
  const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  const getSnapshot = (): ResourceState<T> => state;

  const useStore = (): ResourceState<T> =>
    useSyncExternalStore(subscribe, getSnapshot);

  // ── Actions ──────────────────────────────────────────────────────────────

  let loadPromise: Promise<void> | null = null;

  const load = async (force = false): Promise<void> => {
    if (state.loaded && !force) return;
    if (loadPromise && !force) return loadPromise;

    setState({ loading: true, error: null });
    loadPromise = (async () => {
      try {
        const response = await http.get<T[]>(resourceUri);
        setState({ data: response.data, loaded: true, loading: false });
      } catch (e: any) {
        setState({ loading: false, error: e.message ?? 'Erreur de chargement' });
      } finally {
        loadPromise = null;
      }
    })();
    return loadPromise;
  };

  // ── In-memory getters (no API call) ──────────────────────────────────────

  const getById = (id: string): T | undefined =>
    state.data.find(i => i.id === id);

  const getFiltered = (predicate: (item: T) => boolean): T[] =>
    state.data.filter(predicate);

  // ── Mutations ────────────────────────────────────────────────────────────

  const save = async (item: T): Promise<T> => {
    setState({ working: true, error: null });
    try {
      const response = item.id
        ? await http.put<T>(`${resourceUri}/${item.id}`, item)
        : await http.post<T>(resourceUri, item);
      const saved = response.data;
      setState({
        working: false,
        data: item.id
          ? state.data.map(i => (i.id === saved.id ? saved : i))
          : [...state.data, saved],
      });
      return saved;
    } catch (e: any) {
      setState({ working: false, error: e.message ?? "Erreur d'enregistrement" });
      throw e;
    }
  };

  const deleteById = async (id: string): Promise<void> => {
    setState({ working: true, error: null });
    try {
      await http.delete(`${resourceUri}/${id}`);
      setState({
        working: false,
        data: state.data.filter(item => item.id !== id),
      });
    } catch (e: any) {
      setState({ working: false, error: e.message ?? 'Erreur de suppression' });
      throw e;
    }
  };

  // ── WebSocket patch helpers (called by useStoreUpdater) ──────────────────

  const applyCreate = (item: T) => {
    if (item.id && state.data.find(i => i.id === item.id)) return;
    setState({ data: [...state.data, item] });
  };

  const applyUpdate = (item: T) => {
    const exists = item.id && state.data.some(i => i.id === item.id);
    if (exists) {
      setState({ data: state.data.map(i => (i.id && i.id === item.id ? item : i)) });
    } else if (state.loaded) {
      setState({ data: [...state.data, item] });
    }
  };

  const applyDelete = (id: string) => {
    setState({ data: state.data.filter(i => i.id !== id) });
  };

  return { useStore, getSnapshot, load, getById, getFiltered, save, deleteById, applyCreate, applyUpdate, applyDelete };
}
